import React, { useState } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FaArrowLeft, FaImage, FaXmark, FaGlobe } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import { blogService } from '../../services/blogService';
import { showToast } from '../../utils/toast';
import './CreateBlog.css';
import '../common/JoditEditor.css';
import RichTextEditor from '../common/RichTextEditor';

const CreateBlog = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [blogData, setBlogData] = useState({
    title: '',
    content: '',
    featured_image: null
  });
  const [imagePreview, setImagePreview] = useState('');

  const handleInputChange = (field, value) => {
    setBlogData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Store the actual file object
      handleInputChange('featured_image', file);

      // Create preview for display
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    handleInputChange('featured_image', null);
    setImagePreview('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!blogData.title.trim()) {
      showToast.error('Please enter a title for the blog post');
      return;
    }

    if (!blogData.content.trim()) {
      showToast.error('Please enter content for the blog post');
      return;
    }

    setLoading(true);
    try {
      await blogService.createBlog(blogData);
      showToast.success('Blog post created successfully!');
      navigate('/admin-dashboard/blog-management');
    } catch (error) {
      console.error('Error creating blog:', error);
      showToast.error('Failed to create blog post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wordpress-admin-page">
      <Container fluid className="px-4 py-3">
        {/* WordPress-style Header */}
        <div className="wp-header">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h1 className="wp-page-title">Add New Post</h1>
              <p className="wp-page-description">Create a new blog post</p>
            </div>
            <Button
              variant="outline-secondary"
              onClick={() => navigate('/admin-dashboard/blog-management')}
              className="wp-back-button"
            >
              <FaArrowLeft className="me-2" />
              Back to Posts
            </Button>
          </div>
        </div>

        <Row>
          {/* Main Content Area */}
          <Col lg={8}>
            <div className="wp-main-content">
              {/* Title Field */}
              <div className="wp-title-field mb-4">
                <input
                  type="text"
                  className="wp-title-input"
                  placeholder="Add title"
                  value={blogData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                />
              </div>

              {/* Jodit Rich Text Editor */}
              <div className="wp-content-editor">
                <RichTextEditor
                  value={blogData.content}
                  onChange={(content) => handleInputChange('content', content)}
                  placeholder="Write your content here..."
                />
              </div>
            </div>
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            <div className="wp-sidebar">
              {/* Publish Box */}
              <div className="wp-publish-box">
                <div className="wp-publish-box-header">
                  <h3>Publish</h3>
                </div>
                <div className="wp-publish-box-content">
                  <div className="wp-publish-actions">
                    <Button
                      variant="primary"
                      size="lg"
                      className="wp-publish-button w-100 mb-2"
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          Publishing...
                        </>
                      ) : (
                        <>
                          <FaGlobe className="me-2" />
                          Publish
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Featured Image Box */}
              <div className="wp-featured-image-box">
                <div className="wp-featured-image-box-header">
                  <h3>Featured Image</h3>
                </div>
                <div className="wp-featured-image-box-content">
                  {!imagePreview ? (
                    <div className="wp-image-upload-area-small">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="wp-file-input"
                        id="featured-image"
                      />
                      <label htmlFor="featured-image" className="wp-upload-label-small">
                        <FaImage className="wp-upload-icon-small" />
                        <span>Set featured image</span>
                      </label>
                    </div>
                  ) : (
                    <div className="wp-image-preview-small">
                      <img
                        src={imagePreview}
                        alt="Featured"
                        className="wp-preview-img-small"
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        className="wp-remove-image-small"
                        onClick={handleRemoveImage}
                      >
                        <FaXmark />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CreateBlog; 